import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense, ReactNode } from "react";
import { BrandedLoader } from "@/components/shared/BrandedLoader";
import { AdminLayout } from "./components/layout/AdminLayout";
import { DashboardPage } from "./pages/Dashboard";
import { LoginPage } from "./pages/Login";
import { MFAVerifyPage } from "./pages/MFAVerify";
import { SetNewPasswordPage } from "./pages/SetNewPassword";
import { RequireAuth } from "./components/shared/RequireAuth";
import { RequirePermission } from "./components/shared/RequirePermission";
import { RequireFeature } from "@/components/shared/RequireFeature";
import { RequirePlatform } from "@/components/shared/RequirePlatform";
import {
  RequireDataAdmin,
  RequireSuperAdmin,
} from "./components/shared/RequireSuperAdmin";
import { PERMISSIONS } from "./utils/permissions";

// Auto-retry on chunk load failure (stale deploys)
function lazyRetry<T extends { default: React.ComponentType<unknown> }>(
  loader: () => Promise<T>,
): React.LazyExoticComponent<T["default"]> {
  return lazy(() =>
    loader().catch(() => {
      const hasReloaded = sessionStorage.getItem("chunk_reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return new Promise<T>(() => {}); // never resolves — reload handles it
      }
      sessionStorage.removeItem("chunk_reload");
      return loader(); // second attempt after reload
    }),
  );
}

// Lazy-loaded pages
const PropertiesPage = lazyRetry(() => import("./pages/Properties"));
const PropertyDetailPage = lazyRetry(() => import("./pages/PropertyDetail"));
const ProjectsPage = lazyRetry(() => import("./pages/Projects"));
const BuildingsPage = lazyRetry(() => import("./pages/Buildings"));
const BuildingDetailPage = lazyRetry(() => import("./pages/BuildingDetail"));
const ProjectDetailPage = lazyRetry(() => import("./pages/ProjectDetail"));
const TransactionsPage = lazyRetry(() => import("./pages/Transactions"));
const PropertyLookupPage = lazyRetry(() => import("./pages/PropertyLookup"));
const PropertyEditPage = lazyRetry(() => import("./pages/PropertyEdit"));
const QualityReviewPage = lazyRetry(() => import("./pages/QualityReview"));
const ClientsPage = lazyRetry(() => import("./pages/Clients"));
const ClientDetailPage = lazyRetry(() => import("./pages/ClientDetail"));
const AgentsPage = lazyRetry(() => import("./pages/Agents"));
const AgentDetailPage = lazyRetry(() => import("./pages/AgentDetail"));
const ListingAgentsPage = lazyRetry(() => import("./pages/ListingAgents"));
const ListingAgentDetailPage = lazyRetry(() => import("./pages/ListingAgentDetail"));
const PropertyOwnerDetailPage = lazyRetry(() => import("./pages/PropertyOwnerDetail"));
const EjariContractsPage = lazyRetry(() => import("./pages/EjariContracts"));
const RentalPropertiesPage = lazyRetry(() => import("./pages/RentalProperties"));
const RentalPropertyDetailPage = lazyRetry(() => import("./pages/RentalPropertyDetail"));
const YieldArbitragePage = lazyRetry(() => import("./pages/YieldArbitrage"));
const RentalMarketPage = lazyRetry(() => import("./pages/RentalMarket"));
const Tier2ReviewPage = lazyRetry(() => import("./pages/Tier2Review"));
// Investor section (ported from the client portal — shared public read APIs)
const MarketDashboardPage = lazyRetry(() => import("./pages/market/index"));
const AreaSheetPage = lazyRetry(() => import("./pages/market/AreaSheet"));
const OffPlanPage = lazyRetry(() => import("./pages/market/OffPlan"));
const OffPlanLifecyclePage = lazyRetry(() => import("./pages/OffPlanLifecycle"));
const CommunitiesPage = lazyRetry(() => import("./pages/market/Communities"));
const CommunityDetailPage = lazyRetry(() => import("./pages/market/CommunityDetail"));
const PriceTerrainPage = lazyRetry(() => import("./pages/market/PriceTerrain"));
const FlightMapPage = lazyRetry(() => import("./pages/market/FlightMap"));
const AskSearchPage = lazyRetry(() => import("./pages/market/AskSearch"));
const CorrectedLocationsPage = lazyRetry(() => import("./pages/CorrectedLocations"));
const DuplicateReviewPage = lazyRetry(() => import("./pages/DuplicateReview"));
const ProjectSheetPage = lazyRetry(() => import("./pages/market/ProjectSheet"));
const SecondaryMapPage = lazyRetry(() => import("./pages/market/SecondaryMap"));
const EducationPage = lazyRetry(() => import("./pages/Education"));
const MotivatedSellersPage = lazyRetry(() => import("./pages/market/MotivatedSellers"));
const DistressedDealsPage = lazyRetry(() => import("./pages/market/DistressedDeals"));
const BestDealsPage = lazyRetry(() => import("./pages/market/BestDeals"));
const RentalMarketMapPage = lazyRetry(() => import("./pages/market/RentalMarketMap"));
const ApprovedProjectsPage = lazyRetry(() => import("./pages/market/ApprovedProjects"));
const PhotoClearancePage = lazyRetry(() => import("./pages/market/PhotoClearance"));
const TransactionDetailPage = lazyRetry(() => import("./pages/market/TransactionDetail"));
const VehicleDetailPage = lazyRetry(() => import("./pages/VehicleDetail"));
const VehicleDealersPage = lazyRetry(() => import("./pages/VehicleDealers"));
const VehicleDealerDetailPage = lazyRetry(() => import("./pages/VehicleDealerDetail"));
const ContentPage = lazyRetry(() => import("./pages/Content"));
const ContentEditPage = lazyRetry(() => import("./pages/ContentEdit"));
const NewsletterPage = lazyRetry(() => import("./pages/Newsletter"));
const NewsletterEditPage = lazyRetry(() => import("./pages/NewsletterEdit"));
const DataQualityPage = lazyRetry(() => import("./pages/DataQuality"));
const ModelsPage = lazyRetry(() => import("./pages/Models"));
const BillingPage = lazyRetry(() => import("./pages/Billing"));
const SettingsPage = lazyRetry(() => import("./pages/Settings"));
const PlatformUsersPage = lazyRetry(() => import("./pages/PlatformUsers"));
const PlatformUserDetailPage = lazyRetry(() => import("./pages/PlatformUserDetail"));
const UserGroupsPage = lazyRetry(() => import("./pages/UserGroups"));
const TenantActivityPage = lazyRetry(() => import("./pages/TenantActivity"));
const UserGroupDetailPage = lazyRetry(() => import("./pages/UserGroupDetail"));
const UsersPage = lazyRetry(() => import("./pages/Users"));
const RolesPage = lazyRetry(() => import("./pages/Roles"));
const AlertsPage = lazyRetry(() => import("./pages/Alerts"));
const OperationsDashboardPage = lazyRetry(() => import("./pages/OperationsDashboard"));
const PhotoEnhancerPage = lazyRetry(() => import("./pages/PhotoEnhancer"));
const DubaiDailyBriefingPage = lazyRetry(() => import("./pages/DubaiDailyBriefing"));
const VideoSummaryPage = lazyRetry(() => import("./pages/VideoSummary"));
const VideoDownloadPage = lazyRetry(() => import("./pages/VideoDownload"));
const LyricVideoPage = lazyRetry(() => import("./pages/LyricVideo"));
const ClipLibraryPage = lazyRetry(() => import("./pages/ClipLibrary"));
const GeneratedVideosPage = lazyRetry(() => import("./pages/GeneratedVideos"));
const ScriptVideosPage = lazyRetry(() => import("./pages/ScriptVideos"));
const AutomatedAccountsPage = lazyRetry(() => import("./pages/AutomatedAccounts"));
const AutomatedAccountDetailPage = lazyRetry(
  () => import("./pages/AutomatedAccountDetail"),
);
const AuditLogPage = lazyRetry(() => import("./pages/AuditLog"));
const ProspectPipelinePage = lazyRetry(() => import("./pages/leads/ProspectPipeline"));
const ProspectListPage = lazyRetry(() => import("./pages/leads/ProspectList"));
const ProspectProfilePage = lazyRetry(() => import("./pages/leads/ProspectProfile"));
const OutreachMonitorPage = lazyRetry(() => import("./pages/leads/OutreachMonitor"));
const PartnersPage = lazyRetry(() => import("./pages/leads/Partners"));
const CompliancePage = lazyRetry(() => import("./pages/leads/Compliance"));
const ReportsPage = lazyRetry(() => import("./pages/leads/Reports"));
const VisitsPage = lazyRetry(() => import("./pages/leads/Visits"));
const ScoringDashboardPage = lazyRetry(() => import("./pages/leads/ScoringDashboard"));
const VehiclesPage = lazyRetry(() => import("./pages/Vehicles"));
const PlatformLogsPage = lazyRetry(() => import("./pages/PlatformLogs"));
const ScraperLiveFeedPage = lazyRetry(() => import("./pages/ScraperLiveFeed"));
const DubaiPulsePage = lazyRetry(() => import("./pages/dubai-pulse"));
const DubaiPulseSectorPage = lazyRetry(() => import("./pages/dubai-pulse/SectorPage"));
const DubaiPulseEventsPage = lazyRetry(() => import("./pages/dubai-pulse/Events"));
const DubaiPulseEventsMapPage = lazyRetry(() => import("./pages/dubai-pulse/EventsMap"));

function LazyPage({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<BrandedLoader className="p-8" />}>{children}</Suspense>
  );
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/mfa", element: <MFAVerifyPage /> },
  { path: "/set-new-password", element: <SetNewPasswordPage /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <DashboardPage />
          </RequirePermission>
        ),
      },
      {
        path: "properties",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <PropertiesPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      // --- Investor section (Market Intelligence + Off-Plan Focus) ---
      {
        path: "market",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <MarketDashboardPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/area/:name",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <AreaSheetPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "data/duplicates",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.PROPERTIES_EDIT}>
            <LazyPage>
              <DuplicateReviewPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "data/corrected-locations",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.PROPERTIES_EDIT}>
            <LazyPage>
              <CorrectedLocationsPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "market/terrain",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <PriceTerrainPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/ask",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <AskSearchPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/sky",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <FlightMapPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/communities",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <CommunitiesPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/community/:slug",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <CommunityDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // BEFORE the `:view` sibling below. A literal path declared after a
        // parameterised one is matched as the parameter (pattern 25), which
        // here would render the Projects page for "lifecycle" rather than
        // this board.
        path: "market/offplan/lifecycle",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <OffPlanLifecyclePage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Projects | Map | Market pulse as real pages (MPPI-00150). The
        // optional segment keeps /market/offplan working as the Projects
        // default rather than forcing a redirect.
        path: "market/offplan/:view",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <OffPlanPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/offplan",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <OffPlanPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "education",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.CONTENT_VIEW}>
            <LazyPage>
              <EducationPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "market/approved-projects",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ApprovedProjectsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/rental-map",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <RentalMarketMapPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/photo-clearance",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <PhotoClearancePage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "market/distressed",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <DistressedDealsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/best-deals",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <BestDealsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/motivated-sellers",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <MotivatedSellersPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/secondary",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <SecondaryMapPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/offplan/project/:slug",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ProjectSheetPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "market/transaction/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <TransactionDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "properties/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <PropertyDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "properties/:id/edit",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_EDIT}>
            <LazyPage>
              <PropertyEditPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "buildings",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <BuildingsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "buildings/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <BuildingDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "projects",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ProjectsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "projects/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ProjectDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "properties/quality-review",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_QUALITY_REVIEW}>
            <LazyPage>
              <QualityReviewPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "property-lookup",
        element: (
          // Super admins of ANY tenant (MPPI-00612, widening MPPI-00321).
          // The operator opened owner lookup to tenant super admins with a
          // shared owner register. Roles below super admin still never fetch
          // the chunk, because RequireSuperAdmin renders its fallback instead.
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.PROPERTY_LOOKUP_VIEW}>
              <LazyPage>
                <PropertyLookupPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "transactions",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <TransactionsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "clients",
        element: (
          <RequirePermission required={PERMISSIONS.CLIENTS_VIEW}>
            <LazyPage>
              <ClientsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "clients/:id",
        element: (
          <RequirePermission required={PERMISSIONS.CLIENTS_VIEW}>
            <LazyPage>
              <ClientDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "agents",
        element: (
          <RequirePermission required={PERMISSIONS.AGENTS_VIEW}>
            <LazyPage>
              <AgentsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "agents/:id",
        element: (
          <RequirePermission required={PERMISSIONS.AGENTS_VIEW}>
            <LazyPage>
              <AgentDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "listing-agents",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ListingAgentsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "listing-agents/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <ListingAgentDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // MPPI-00021 — the owner register now renders beneath Title Lookup.
        // Redirect rather than delete: the old URL is bookmarked and linked
        // from elsewhere in the admin, and a 404 would look like the feature
        // had been removed. The detail route below is untouched, because
        // owner rows still link to it.
        path: "property-owners",
        element: <Navigate to="/property-lookup" replace />,
      },
      {
        path: "property-owners/:id",
        element: (
          // Same audience as the owner-register API (MPPI-00612).
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
              <LazyPage>
                <PropertyOwnerDetailPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "ejari",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <EjariContractsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "rental-properties",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <RentalPropertiesPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "rental-properties/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <RentalPropertyDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "yield-arbitrage",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
              <LazyPage>
                <YieldArbitragePage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "rental-market",
        element: (
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <RentalMarketPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "tier2-review",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.PROPERTIES_VIEW}>
            <LazyPage>
              <Tier2ReviewPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "content",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.CONTENT_VIEW}>
            <LazyPage>
              <ContentPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "content/:type/:id/edit",
        element: (
          <RequirePermission required={PERMISSIONS.CONTENT_EDIT}>
            <LazyPage>
              <ContentEditPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "newsletter",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.NEWSLETTER_VIEW}>
            <LazyPage>
              <NewsletterPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "newsletter/:id/edit",
        element: (
          <RequirePermission required={PERMISSIONS.NEWSLETTER_EDIT}>
            <LazyPage>
              <NewsletterEditPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "data-quality",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.DATA_QUALITY_VIEW}>
            <LazyPage>
              <DataQualityPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "models",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.MODELS_VIEW}>
            <LazyPage>
              <ModelsPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "billing",
        element: (
          <RequirePermission required={PERMISSIONS.BILLING_VIEW}>
            <LazyPage>
              <BillingPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "settings",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <SettingsPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "platform-users",
        element: (
          <RequirePermission required={PERMISSIONS.PLATFORM_USERS_VIEW}>
            <LazyPage>
              <PlatformUsersPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "platform-users/:id",
        element: (
          <RequirePermission required={PERMISSIONS.PLATFORM_USERS_VIEW}>
            <LazyPage>
              <PlatformUserDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Renamed from /user-groups (MPPI-00313). Kept as a redirect rather
        // than removed: the old path is in browser histories and in at least
        // one tracker entry, and a 404 on a bookmark reads as a broken deploy.
        path: "user-groups",
        element: <Navigate to="/tenants" replace />,
      },
      {
        path: "user-groups/:id",
        element: <Navigate to="/tenants" replace />,
      },
      {
        // Usage per tenant, for offline invoicing (MPPI-00326). Platform-only:
        // one tenant seeing another's usage is the whole reason the tenancy
        // work exists.
        path: "tenant-activity",
        element: (
          <RequirePlatform>
            <RequirePermission required={PERMISSIONS.BILLING_VIEW}>
              <LazyPage>
                <TenantActivityPage />
              </LazyPage>
            </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "tenants",
        element: (
          <RequirePermission required={PERMISSIONS.ROLES_MANAGE}>
            <LazyPage>
              <UserGroupsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "tenants/:id",
        element: (
          <RequirePermission required={PERMISSIONS.ROLES_MANAGE}>
            <LazyPage>
              <UserGroupDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "users",
        element: (
          <RequireSuperAdmin>
            <RequirePermission required={PERMISSIONS.USERS_VIEW}>
              <LazyPage>
                <UsersPage />
              </LazyPage>
            </RequirePermission>
          </RequireSuperAdmin>
        ),
      },
      {
        path: "roles",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.ROLES_MANAGE}>
            <LazyPage>
              <RolesPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        // Operator F-002: Operations Dashboard sits above Alerts in
        // both the sidebar and the route registration so the menu
        // ordering matches the routing.
        path: "operations",
        element: (
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <OperationsDashboardPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "alerts",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.ALERTS_VIEW}>
            <LazyPage>
              <AlertsPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        // Tools > Photo Enhancer — admin-only AI photo enhancement.
        // Backend gates by role (SUPER_ADMIN/DATA_ADMIN/ANALYST);
        // sidebar visibility uses DASHBOARD_VIEW because every admin
        // role has it (CO/AGENT will see the link but get a 403 from
        // the backend if they upload — acceptable for v1).
        path: "tools/photo-enhancer",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <PhotoEnhancerPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Tools > Dubai Daily Briefing — Front Page view of the latest
        // edition + previous editions. Date param is optional; without
        // it we render the most recent published edition.
        path: "tools/dubai-daily-briefing",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <DubaiDailyBriefingPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "tools/dubai-daily-briefing/:date",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <DubaiDailyBriefingPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Tools > Ad-hoc Video (MPPI-00573). Platform-only: the render spends
        // HeyGen credits against the PLATFORM's account, not a tenant's, so
        // Switched on per tenant (MPPI-00642): HouseKey always, another
        // tenant when the platform has enabled it. Gated at the ROUTE, not
        // just the API, so a tenant without it never fetches the chunk
        // (CLAUDE.md pattern 39).
        path: "tools/ad-hoc-video",
        element: (
          <RequireFeature feature="adhoc_videos">
            <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
              <LazyPage>
                <ScriptVideosPage />
              </LazyPage>
            </RequirePermission>
          </RequireFeature>
        ),
      },
      {
        // Tools > Generated Videos (cross-property index of showcase renders)
        path: "tools/generated-videos",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <GeneratedVideosPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Social Media Tools > Video Summary
        path: "tools/video-summary",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <VideoSummaryPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Social Media Tools > Clip Library (MPPI-00926)
        path: "tools/clip-library",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <ClipLibraryPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Social Media Tools > Lyric Video (MPPI-00914)
        path: "tools/lyric-video",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <LyricVideoPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Social Media Tools > Video Download
        path: "tools/video-download",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <VideoDownloadPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // Social Media Tools > Automated Accounts
        path: "tools/automated-accounts",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <AutomatedAccountsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "tools/automated-accounts/:id",
        element: (
          <RequirePermission required={PERMISSIONS.DASHBOARD_VIEW}>
            <LazyPage>
              <AutomatedAccountDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "audit-log",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.AUDIT_VIEW}>
            <LazyPage>
              <AuditLogPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        // DCP T-013: city-pulse source-registry view. Phase 0 stub
        // hits the city_pulse router on the API; Phase 1 lands real
        // data once T-016 has populated data_sources on staging.
        path: "dubai-pulse",
        element: (
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <DubaiPulsePage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        // F-016 admin-parity — same per-sector drill-in as the
        // investor portal. The Dashboard tab on /dubai-pulse links here.
        path: "dubai-pulse/sectors/:sector",
        element: (
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <DubaiPulseSectorPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "dubai-pulse/events",
        element: (
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <DubaiPulseEventsPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "dubai-pulse/events/map",
        element: (
          <RequirePermission required={PERMISSIONS.SETTINGS_VIEW}>
            <LazyPage>
              <DubaiPulseEventsMapPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "platform-logs",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.LOGS_VIEW}>
            <LazyPage>
              <PlatformLogsPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "scraper-live-feed",
        element: (
          <RequirePlatform>
          <RequirePermission required={PERMISSIONS.LOGS_VIEW}>
            <LazyPage>
              <ScraperLiveFeedPage />
            </LazyPage>
          </RequirePermission>
          </RequirePlatform>
        ),
      },
      {
        path: "vehicles",
        element: (
          <RequirePermission required={PERMISSIONS.VEHICLES_VIEW}>
            <LazyPage>
              <VehiclesPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "vehicles/:id",
        element: (
          <RequirePermission required={PERMISSIONS.VEHICLES_VIEW}>
            <LazyPage>
              <VehicleDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "vehicle-dealers",
        element: (
          <RequirePermission required={PERMISSIONS.VEHICLES_VIEW}>
            <LazyPage>
              <VehicleDealersPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "vehicle-dealers/:id",
        element: (
          <RequirePermission required={PERMISSIONS.VEHICLES_VIEW}>
            <LazyPage>
              <VehicleDealerDetailPage />
            </LazyPage>
          </RequirePermission>
        ),
      },
      {
        path: "leads/pipeline",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <ProspectPipelinePage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/prospects",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <ProspectListPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/prospects/:id",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <ProspectProfilePage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/outreach",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <OutreachMonitorPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/partners",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <PartnersPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/compliance",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_COMPLIANCE}>
              <LazyPage>
                <CompliancePage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/reports",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <ReportsPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/visits",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <VisitsPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
      {
        path: "leads/scoring",
        element: (
          <RequireDataAdmin>
            <RequirePermission required={PERMISSIONS.LEADS_VIEW}>
              <LazyPage>
                <ScoringDashboardPage />
              </LazyPage>
            </RequirePermission>
          </RequireDataAdmin>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
